import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Home, PlusCircle, Users, Wallet, Menu, X, Shield, LogOut, Volume2, VolumeX } from "lucide-react";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { sound } from "@/lib/sound";

const TABS = [
  { to: "/", icon: Home, label: "Home" },
  { to: "/create", icon: PlusCircle, label: "Create" },
  { to: "/join", icon: Users, label: "Join" },
  { to: "/payouts", icon: Wallet, label: "Payouts" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAdmin = ["owner_admin", "admin"].includes(user?.role);
  const [menuOpen, setMenuOpen] = useState(false);
  const [muted, setMuted] = useState(sound.isMuted());

  const doLogout = () => {
    logout();
    navigate("/start");
  };

  return (
    <div className="min-h-screen flex flex-col items-center">
      <div className="w-full max-w-[460px] flex flex-col min-h-screen relative">
        {/* Top bar: logo + menu */}
        <header className="flex items-center justify-between px-5 pt-5 pb-2">
          <Brand size="sm" />
          <button
            data-testid="menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            className="h-10 w-10 grid place-items-center text-white/80 hover:text-white"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </header>

        {/* Slide-down menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="absolute right-4 top-16 z-40 w-56 hp-glass rounded-2xl p-2 backdrop-blur-xl bg-[#0e0a16]/95"
            >
              <div className="px-3 py-2 border-b border-white/5 mb-1">
                <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                {!user?.username?.startsWith("guest_") && (
                  <p className="text-xs text-muted-foreground truncate">@{user?.username}</p>
                )}
              </div>
              <button
                data-testid="mute-toggle"
                onClick={() => setMuted(sound.toggleMuted())}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5"
              >
                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                {muted ? "Sound off" : "Sound on"}
              </button>
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMenuOpen(false)}
                  data-testid="menu-admin"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5"
                >
                  <Shield size={18} /> Admin
                </Link>
              )}
              <button
                data-testid="logout-btn"
                onClick={doLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5"
              >
                <LogOut size={18} />
                {user?.username?.startsWith("guest_") ? "New session" : "Sign out"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <main className="flex-1 px-5 pb-32">
          <AnimatePresence mode="wait">
            <motion.div
              key={pathname}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Bottom tab bar */}
        <nav className="fixed bottom-4 inset-x-0 z-30 px-5 flex justify-center pointer-events-none">
          <div className="hp-tabbar w-full max-w-[420px] flex justify-around py-2.5 px-2 pointer-events-auto">
            {TABS.map((it) => {
              const active = pathname === it.to;
              const Icon = it.icon;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  onClick={() => sound.play("nav")}
                  data-testid={`tab-${it.label.toLowerCase()}`}
                  className="relative flex flex-col items-center gap-1 px-4 py-1"
                >
                  <Icon
                    size={23}
                    className={active ? "text-[#c084fc]" : "text-muted-foreground"}
                    style={active ? { filter: "drop-shadow(0 0 8px rgba(192,132,252,0.8))" } : undefined}
                  />
                  <span
                    className={`text-[11px] font-semibold ${
                      active ? "text-[#c084fc]" : "text-muted-foreground"
                    }`}
                  >
                    {it.label}
                  </span>
                  {active && (
                    <motion.span
                      layoutId="tab-active"
                      className="absolute -bottom-0.5 h-0.5 w-6 rounded-full bg-[#c084fc]"
                    />
                  )}
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
